// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// calltones — WebAudio call-signalling tones (no audio assets): the caller's "calling…"
// ringback, the callee's incoming ringtone, plus short connect / busy (no-answer) / hangup
// cues. Uses ONE shared AudioContext, created + resumed only inside a user gesture
// (browsers block autoplay otherwise); every play is a silent no-op until it's unlocked.

let ctx: AudioContext | null = null;
let armed: { pattern: () => void; periodMs: number } | null = null; // a ring waiting for the context to unlock

// Sound preferences (Profile → Notifications). "call" gates every call cue (ring / connect /
// join-leave / reconnect / hangup); "message" gates the incoming-message ding. Both default ON.
export type SoundKind = "call" | "message";
const SOUND_KEY = "zalmoxis.sounds";
function loadSoundPrefs(): Record<SoundKind, boolean> {
  try {
    return { call: true, message: true, ...JSON.parse(localStorage.getItem(SOUND_KEY) ?? "{}") };
  } catch {
    return { call: true, message: true };
  }
}
let soundPrefs = loadSoundPrefs();
export function isSoundEnabled(kind: SoundKind): boolean {
  return soundPrefs[kind];
}
export function setSoundEnabled(kind: SoundKind, on: boolean): void {
  soundPrefs = { ...soundPrefs, [kind]: on };
  try {
    localStorage.setItem(SOUND_KEY, JSON.stringify(soundPrefs));
  } catch {
    /* private mode — just won't persist */
  }
  if (kind === "call" && !on) stopTone(); // silence any looping ring immediately
}

// unlockAudioOnGesture creates/resumes the shared context on the first click/keydown, so
// later tones can play without the browser's autoplay block. Crucially it ALSO replays an
// "armed" ring — a ringtone requested before any gesture (e.g. an incoming call on a tab
// the callee hasn't clicked) starts the instant they interact. Returns a cleanup fn.
export function unlockAudioOnGesture(): () => void {
  const unlock = () => {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    ctx ??= new Ctx();
    const replay = () => {
      if (armed && ctx?.state === "running") {
        const a = armed;
        armed = null;
        startLoop(a.pattern, a.periodMs);
      }
    };
    if (ctx.state === "suspended") void ctx.resume().then(replay);
    else replay();
  };
  window.addEventListener("pointerdown", unlock);
  window.addEventListener("keydown", unlock);
  window.addEventListener("touchstart", unlock);
  return () => {
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
    window.removeEventListener("touchstart", unlock);
  };
}

// beep schedules one tone (with a soft attack/decay envelope) on the shared context. `kind`
// gates it by the Sounds preference (defaults to "call" — every call cue; the message ding
// passes "message").
function beep(freq: number, start: number, dur: number, peak = 0.16, kind: SoundKind = "call"): void {
  if (!soundPrefs[kind]) return;
  if (!ctx || ctx.state !== "running") return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.frequency.value = freq;
  const t = ctx.currentTime + start;
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(peak, t + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.start(t);
  osc.stop(t + dur + 0.03);
}

let loop: ReturnType<typeof setInterval> | null = null;
function startLoop(pattern: () => void, periodMs: number): void {
  stopTone();
  if (!soundPrefs.call) return; // ring cues gated off

  if (!ctx || ctx.state !== "running") {
    armed = { pattern, periodMs }; // no gesture yet — play as soon as the context unlocks
    return;
  }
  pattern();
  loop = setInterval(() => pattern(), periodMs);
}

// stopTone stops any looping tone (ringback / ringtone), incl. one armed but not yet playing.
export function stopTone(): void {
  armed = null;
  if (loop) clearInterval(loop);
  loop = null;
}

// ringback — the "calling…" cadence the CALLER hears while waiting (dual 440/480 Hz, ~1 s
// on every 3 s, like a classic phone).
export function ringback(): void {
  startLoop(() => {
    beep(440, 0, 1, 0.1);
    beep(480, 0, 1, 0.1);
  }, 3000);
}

// ringtone — the incoming-call ring the CALLEE hears (an attention-grabbing two-tone,
// repeated every ~1.5 s).
export function ringtone(): void {
  startLoop(() => {
    beep(880, 0, 0.4);
    beep(988, 0.5, 0.4);
  }, 1500);
}

// playRing — a SINGLE incoming alert (used for an incoming group call, which doesn't ring).
export function playRing(): void {
  beep(880, 0, 0.4);
  beep(988, 0.5, 0.4);
}

// playConnect — a short ascending blip when the call connects.
export function playConnect(): void {
  stopTone();
  beep(660, 0, 0.12);
  beep(880, 0.12, 0.16);
}

// playBusy — the no-answer / busy cue (a descending pair) when an outgoing call fails.
export function playBusy(): void {
  stopTone();
  beep(480, 0, 0.3, 0.14);
  beep(400, 0.35, 0.4, 0.14);
}

// playHangup — a short descending tone when an established call ends.
export function playHangup(): void {
  stopTone();
  beep(520, 0, 0.16, 0.12);
  beep(380, 0.16, 0.22, 0.12);
}

// playJoin — a soft ASCENDING two-note when someone joins the group call (Discord-style; quiet
// so a busy call doesn't get noisy).
export function playJoin(): void {
  beep(587, 0, 0.1, 0.09);
  beep(784, 0.1, 0.16, 0.09);
}

// playLeave — a soft DESCENDING two-note when someone leaves the group call.
export function playLeave(): void {
  beep(784, 0, 0.1, 0.09);
  beep(587, 0.1, 0.16, 0.09);
}

// playReconnecting — a low descending "connection lost" cue when the sync socket drops mid-call
// (the visible "Reconnecting…" badge, made audible so you know why the call went quiet).
export function playReconnecting(): void {
  beep(440, 0, 0.18, 0.13);
  beep(300, 0.2, 0.3, 0.13);
}

// playReconnected — an ascending "back online" cue when the socket recovers.
export function playReconnected(): void {
  beep(440, 0, 0.12, 0.12);
  beep(660, 0.12, 0.18, 0.12);
}

// playMessage — a soft "ding" for an incoming message (a gentle rising two-note). Throttled so a
// burst of messages in a busy channel doesn't machine-gun.
let lastMsgTone = 0;
export function playMessage(): void {
  const now = Date.now();
  if (now - lastMsgTone < 1500) return;
  lastMsgTone = now;
  beep(784, 0, 0.08, 0.08, "message");
  beep(1047, 0.07, 0.16, 0.08, "message");
}
